import React, { useCallback } from 'react';

import { ApolloError } from '@apollo/client';
import { useRouter } from 'expo-router';
import {
  CONTACT_US_ROUTE_KEY,
  getErrorDialogOptions,
  getOrderFailureMessageKey,
} from 'features/checkout/utils/get-error-dialog-options';
import { Typography } from 'ucl/components/text/Typography';

import Link from 'components/link';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { ModalCb } from 'hooks/use-error-modal';
import { routes } from 'navigation/routing';
import { t, tRich } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { GraphQLErrorCodes, isPspRefusedError, parseGraphQLErrorCodes } from 'utils/errors';
import { GraphQLError } from 'utils/network';

export interface IHandleCommitErrorInput {
  commitError: ApolloError | GraphQLError | undefined;
  dismissErrorDialog: () => void;
  errorMsgId?: TranslationKey;
  onCoolingPeriodConfirm: () => void;
  openErrorDialog: ModalCb;
  confirmationTextId?: 'placeOrder' | 'continue';
  fdAccountId: string;
}

export const useHandleCommitError = ({
  commitError,
  dismissErrorDialog,
  errorMsgId,
  onCoolingPeriodConfirm,
  openErrorDialog,
  confirmationTextId = 'placeOrder',
  fdAccountId,
}: IHandleCommitErrorInput) => {
  const router = useRouter();

  const handleCommitError = useCallback(
    (error: ApolloError | GraphQLError) => {
      if (isPspRefusedError(error)) {
        return;
      }

      let errorDialog;
      const errorCode = parseGraphQLErrorCodes(error)[0]?.errorCode;

      if (errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD) {
        errorDialog = {
          body: t('errorDialogCooldownMessage'),
          title: t('errorDialogCooldownTitle'),
          message: t('errorDialogCooldownMessage'),
          dismissText: t('viewRecentOrders'),
          confirmationText: t(confirmationTextId),
          onDismiss: () => router.replace(routes.orders),
          onConfirm: () => {
            dismissErrorDialog();
            onCoolingPeriodConfirm();
          },
        };
      } else {
        const errorMessageId = errorMsgId || getOrderFailureMessageKey(fdAccountId, error);
        const body = (
          <Typography>
            {tRich(errorMessageId, {
              link: (
                <Link isInline isUnderlined to={CONTACT_US_ROUTE_KEY} onPress={dismissErrorDialog}>
                  {t('here')}
                </Link>
              ),
            })}
          </Typography>
        );
        errorDialog = getErrorDialogOptions({
          body,
          error,
          errorMsgId: errorMessageId,
        });
      }

      openErrorDialog(errorDialog);
    },
    [
      openErrorDialog,
      confirmationTextId,
      router,
      dismissErrorDialog,
      onCoolingPeriodConfirm,
      errorMsgId,
      fdAccountId,
    ]
  );

  useEffectOnUpdates(() => {
    if (commitError) {
      handleCommitError(commitError);
    }
  }, [commitError]);

  return { handleCommitError };
};
